// components/screener/FilterBar.jsx
import React, { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
    Box, Button, Stack, Typography, Menu, MenuItem, Chip, IconButton, Tooltip
} from '@mui/material';
import {
    Add as AddIcon,
    Search as SearchIcon,
    Close as ClearIcon,
    FileDownload as ExportIcon,
    KeyboardArrowDown as ChevronIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { predefinedScreens } from './config/predefinedScreens';

const FilterBar = ({
    activeFilterCount,
    selectedScreenName,
    onAddFilter,
    onSearch,
    onClear,
    onExport,
    showExport,
    onPredefinedScreen,
    isFiltersExpanded,
    onToggleFilters,
    isLoading,
    totalResults,
}) => {
    const theme = useTheme();
    const [screensAnchor, setScreensAnchor] = useState(null);

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            py: 1.5,
        }}>
            {/* Left side */}
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {/* Filter count + collapse toggle */}
                {activeFilterCount > 0 && (
                    <Chip
                        label={`${activeFilterCount} Filter${activeFilterCount > 1 ? 's' : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onClick={onToggleFilters}
                        onDelete={onToggleFilters}
                        deleteIcon={isFiltersExpanded
                            ? <ExpandLessIcon sx={{ fontSize: '18px !important' }} />
                            : <ExpandMoreIcon sx={{ fontSize: '18px !important' }} />
                        }
                        sx={{
                            fontWeight: 600,
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            borderRadius: '8px',
                        }}
                    />
                )}

                {/* Popular Screens */}
                <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setScreensAnchor(e.currentTarget)}
                    endIcon={<ChevronIcon />}
                    sx={{
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        textTransform: 'none',
                        borderColor: theme.palette.divider,
                        color: selectedScreenName ? theme.palette.primary.main : theme.palette.text.secondary,
                    }}
                >
                    {selectedScreenName || 'Screens'}
                </Button>

                <Menu
                    anchorEl={screensAnchor}
                    open={Boolean(screensAnchor)}
                    onClose={() => setScreensAnchor(null)}
                    PaperProps={{
                        sx: {
                            mt: 0.5,
                            maxHeight: 400,
                            width: 260,
                            bgcolor: theme.palette.mode === 'dark'
                                ? 'rgba(10, 25, 41, 0.97)' : theme.palette.background.paper,
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                        },
                    }}
                >
                    {predefinedScreens.map((screen) => (
                        <MenuItem
                            key={screen.id}
                            onClick={() => {
                                setScreensAnchor(null);
                                onPredefinedScreen(screen);
                            }}
                            sx={{
                                fontSize: '0.85rem',
                                py: 1.25,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {screen.name}
                        </MenuItem>
                    ))}
                </Menu>

                {/* Add Filter */}
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAddFilter}
                    sx={{ fontSize: '0.8rem', fontWeight: 600 }}
                >
                    Add Filter
                </Button>
            </Stack>

            {/* Right side */}
            <Stack direction="row" alignItems="center" spacing={1}>
                {/* Result count */}
                {totalResults > 0 && (
                    <Typography variant="body2" sx={{
                        color: theme.palette.text.secondary,
                        fontSize: '0.82rem',
                        fontWeight: 500,
                        mr: 0.5,
                    }}>
                        {totalResults.toLocaleString()} results
                    </Typography>
                )}

                {showExport && (
                    <Tooltip title="Export to CSV(page)">
                        <IconButton
                            size="small"
                            onClick={onExport}
                            sx={{ color: theme.palette.text.secondary }}
                        >
                            <ExportIcon sx={{ fontSize: 20 }} />
                        </IconButton>
                    </Tooltip>
                )}

                {activeFilterCount > 0 && (
                    <Button
                        size="small"
                        variant="text"
                        onClick={onClear}
                        startIcon={<ClearIcon sx={{ fontSize: '16px !important' }} />}
                        sx={{
                            fontSize: '0.8rem',
                            color: theme.palette.text.secondary,
                            '&:hover': { color: theme.palette.error.main },
                        }}
                    >
                        Clear
                    </Button>
                )}

                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={onSearch}
                    disabled={isLoading || activeFilterCount === 0}
                    sx={{
                        fontSize: '0.8rem',
                        fontWeight: 700,
                        px: 2.5,
                    }}
                >
                    Search
                </Button>
            </Stack>
        </Box>
    );
};

export default FilterBar;
